import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { recurringGivingManageApi } from '@/services/giving.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Spinner, StatusBadge } from '@/components/ui';
import { LogoMark } from '@/components/logo-mark';

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/**
 * Where a recurring-giving reminder email's "manage this" link goes — pause,
 * resume or cancel with no account, the signed `:token` itself standing in
 * for a session (see `recurring-giving-token.ts`). Reached outside both app
 * shells, the same as `/give/:tenantSlug`.
 */
export function ManageRecurringPage() {
  const { token = '' } = useParams();
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const schedule = useQuery({
    queryKey: ['giving', 'recurring', token],
    queryFn: ({ signal }) => recurringGivingManageApi.get(token, signal),
  });

  const invalidate = (updated: Awaited<ReturnType<typeof recurringGivingManageApi.get>>) => {
    queryClient.setQueryData(['giving', 'recurring', token], updated);
    setError(null);
  };

  const pause = useMutation({
    mutationFn: () => recurringGivingManageApi.pause(token),
    onSuccess: invalidate,
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });
  const resume = useMutation({
    mutationFn: () => recurringGivingManageApi.resume(token),
    onSuccess: invalidate,
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });
  const cancel = useMutation({
    mutationFn: () => recurringGivingManageApi.cancel(token),
    onSuccess: invalidate,
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const data = schedule.data;

  return (
    <main className="auth-page">
      <div className="auth-card">
        <header className="auth-header">
          <LogoMark />
          <h1 className="auth-title">{t('giving.public.recurring.manageTitle')}</h1>
        </header>

        {schedule.isPending && <Spinner label={t('common.states.loading')} />}
        {schedule.isError && <Alert tone="danger">{errorMessage(schedule.error, t)}</Alert>}
        {error && <Alert tone="danger">{error}</Alert>}

        {data && (
          <div className="detail-grid">
            <dl className="detail-grid">
              <div>
                <dt>{t('giving.public.recurring.manageStatus')}</dt>
                <dd>
                  <StatusBadge
                    status={data.status}
                    label={t(`giving.public.recurring.status.${data.status}`)}
                  />
                </dd>
              </div>
              <div>
                <dt>{t('giving.public.fund')}</dt>
                <dd>{t(`giving.fund.${data.fund}`)}</dd>
              </div>
              <div>
                <dt>{t('giving.public.amount', { values: { currency: data.currency } })}</dt>
                <dd dir="ltr">{(data.amount / 100).toFixed(2)}</dd>
              </div>
              <div>
                <dt>{t('giving.public.recurring.frequency')}</dt>
                <dd>{t(`giving.public.recurring.frequencyOption.${data.frequency}`)}</dd>
              </div>
              {data.nextRunAt && (
                <div>
                  <dt>{t('giving.public.recurring.manageNextRun')}</dt>
                  <dd dir="ltr">{new Date(data.nextRunAt).toLocaleDateString(locale)}</dd>
                </div>
              )}
            </dl>

            <div className="page-actions">
              {data.status === 'ACTIVE' && (
                <Button
                  variant="secondary"
                  loading={pause.isPending}
                  onClick={() => pause.mutate()}
                >
                  {t('giving.public.recurring.pause')}
                </Button>
              )}
              {data.status === 'PAUSED' && (
                <Button
                  variant="secondary"
                  loading={resume.isPending}
                  onClick={() => resume.mutate()}
                >
                  {t('giving.public.recurring.resume')}
                </Button>
              )}
              {data.status !== 'CANCELLED' && (
                <Button variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>
                  {t('giving.public.recurring.cancel')}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
